/**
 * SR AR System - Main Entry Point
 * QR-triggered AR poster video player
 */

// Core modules
import * as THREE from 'three';
import { ARSession, SessionState } from './core/session.js';
import { PosterDetector } from './core/detector.js';
import { ARRenderer } from './core/renderer.js';

// Components
import { QRScanner } from './components/qr-scanner.js';
import { CameraView } from './components/camera-view.js';
import { SplashScreen } from './components/splash-screen.js';
import { AudioConsentDialog } from './components/audio-consent-dialog.js';
import { createCTAFromPosterData } from './components/cta-overlay.js';

// UI (unified multi-marker style)
import { FeedbackOverlay } from './ui/feedback-overlay.js';
import { ErrorHandler, ErrorType, isCameraDenied } from './services/error-handler.js';

// Services
import { apiClient } from './services/api-client.js';
import { resolveDomainDefaultUrl } from './utils/domain-defaults.js';
import { posterService } from './services/poster-service.js';
import { VideoController } from './services/video-controller.js';
import { AudioController } from './services/audio-controller.js';
import { MultiMarkerARSession } from './services/multi-marker-ar-session.js';

// Utilities
import { ValidationError } from './utils/validation.js';
// import { PerformanceMonitor } from './utils/performance-monitor.js'; // DISABLED: Causes iOS jitter
import { ObjectPool } from './utils/performance-utils.js';
import { VideoTextureManager } from './core/renderer.js';
import { CameraMotionDetector } from './utils/camera-motion-detector.js';
import {
  checkBrowserCompatibility,
  displayCompatibilityError,
  logCompatibilityInfo,
} from './utils/browser-check.js';
import {
  attachPagehideFlush,
  trackARSessionStart,
  trackCameraGrant,
  trackError,
  trackMarkerDetection,
  trackMarkerEnd,
  trackNetworkSpeed,
  trackPageView,
  trackVideoLoad,
} from './utils/analytics.js';
import { detectNetworkSpeed } from './utils/network-speed.js';
import { resolveAuraConfig } from './effects/video-aura-logic.js';
import { createVideoAura } from './effects/video-aura.js';

/**
 * Global application state
 */
let app = {
  session: null,
  qrScanner: null,
  cameraView: null,
  feedbackOverlay: null, // Unified UI (replaces loadingUI + errorUI + trackingIndicator)
  errorHandler: null, // Error handling logic
  splashScreen: null,
  audioConsentDialog: null,
  audioController: null,
  userAudioChoice: null, // Store user's audio preference
  currentPosterId: null,
  videoElement: null,
  posterDetector: null,
  arRenderer: null,
  videoController: null,
  arVideoElement: null,
  performanceMonitor: null,
  multiMarkerSession: null, // Multi-marker AR session
  vector3Pool: null, // Vector3 object pooling for GC optimization
  videoTextureManager: null, // Video texture update optimization
  motionDetector: null, // Camera motion detection for stable marker selection
  ctaOverlay: null, // CTA overlay component (structured buttons / legacy html)
  aura: null, // Video aura effect (Issue #26 — opt-in via posters.effects.aura)
};

/**
 * Initialize AR Experience
 */
async function initARExperience() {
  try {
    console.log('SR AR System initializing...');

    // Track page view with URL parameters (e.g., ?poster=poster-001)
    trackPageView(window.location.pathname + window.location.search, document.title);

    // ページ離脱で session_end + queue flush（既存の video pause 用 visibilitychange と共存）
    attachPagehideFlush();

    // Detect and track network speed
    const networkSpeed = detectNetworkSpeed();
    trackNetworkSpeed(networkSpeed);

    // Initialize splash screen (but don't show for long - match multi-marker style)
    app.splashScreen = new SplashScreen();
    app.splashScreen.init(document.body);
    app.splashScreen.show();

    // Check browser compatibility first
    const compatibility = checkBrowserCompatibility();
    logCompatibilityInfo(compatibility);

    if (!compatibility.isCompatible) {
      const uiContainer = document.getElementById('ui-container');
      displayCompatibilityError(compatibility, uiContainer);
      return;
    }

    // Initialize UI components (unified multi-marker style)
    const uiContainer = document.getElementById('ui-container');

    // FeedbackOverlay replaces LoadingUI + ErrorUI + TrackingIndicator
    app.feedbackOverlay = new FeedbackOverlay({
      container: document.body,
      autoHide: false,
    });

    // ErrorHandler for structured error management
    app.errorHandler = new ErrorHandler({
      onError: (error, level, userMessage, errorInfo) => {
        if (app.feedbackOverlay && level !== 'info') {
          app.feedbackOverlay.showError(userMessage, errorInfo.troubleshooting || []);
        }
      },
      showConsoleErrors: true,
    });

    app.audioConsentDialog = new AudioConsentDialog();
    app.audioConsentDialog.init(uiContainer);

    // Initialize AR session
    app.session = new ARSession();

    // DISABLED: PerformanceMonitor causes iOS video jitter
    // Reason: recordFrame() logs every frame → console.warn() blocks main thread
    // app.performanceMonitor = new PerformanceMonitor({
    //   enabled: false,
    //   logInterval: 10000,
    //   fpsTarget: 30,
    // });
    app.performanceMonitor = null;

    // Initialize performance optimization components (Phase 1-4)
    app.vector3Pool = new ObjectPool(() => new THREE.Vector3(), 10);
    app.videoTextureManager = new VideoTextureManager();
    app.motionDetector = new CameraMotionDetector({
      stabilityThreshold: 0.005,
      stabilityDuration: 300,
    });
    console.log('✅ Performance optimization components initialized');

    // Check if poster ID is provided in URL
    const urlParams = new URLSearchParams(window.location.search);
    const posterIdFromUrl = urlParams.get('poster');

    if (posterIdFromUrl) {
      // Direct poster access - skip QR scanning
      // Wait for user to tap "体験を始める" before requesting camera permission
      // (avoids surprise camera dialog with no context)
      await app.splashScreen.waitForStart();
      await handleDirectPosterAccess(posterIdFromUrl);
    } else {
      // No poster parameter — hostname に応じて posters.json の _domainDefaults から遷移先を決定。
      // クライアントカスタムドメイン(ar.syukaso.com 等)ではそのクライアントのコーポレートサイトへ、
      // 弊社ドメインや sr-ar.pages.dev では従来通り弊社の紹介記事へ導く。
      const FALLBACK_URL = 'https://enters-lab.co.jp/articles/20251028/';
      let redirectUrl = FALLBACK_URL;
      try {
        const postersJson = await apiClient.loadPostersData();
        redirectUrl = resolveDomainDefaultUrl(window.location.hostname, postersJson, FALLBACK_URL);
      } catch (err) {
        console.warn('Failed to load _domainDefaults, using fallback:', err?.message || err);
      }
      console.log(`🔄 No poster parameter, redirecting to ${redirectUrl}`);
      window.location.href = redirectUrl;
    }
  } catch (error) {
    console.error('Fatal error during AR initialization:', error);
    if (app.feedbackOverlay) {
      app.feedbackOverlay.showError(error.message, ['ページをリロードして再試行してください']);
    }
  }
}

/**
 * Handle direct poster access from URL parameter
 * Skips QR scanning and directly initializes poster detection
 * @param {string} posterId - Poster ID from URL parameter
 */
async function handleDirectPosterAccess(posterId) {
  try {
    app.currentPosterId = posterId;

    // Load poster data
    const posterData = await posterService.preloadPoster(posterId);

    // Update browser tab title per poster entry
    if (posterData.title || posterData.name) {
      document.title = posterData.title || posterData.name;
    }

    // Check if multi-marker poster
    if (posterData.type === 'multi-marker') {
      console.log('🎯 Multi-marker poster detected, using MultiMarkerARSession');
      await handleMultiMarkerPoster(posterId, posterData);
      return;
    }

    // Single-marker poster flow (existing code)
    console.log('🎯 Single-marker poster detected, using legacy flow');

    // Note: MindAR handles camera internally, no need to create video element

    // State transition: IDLE → CAMERA_INIT
    app.session.setState(SessionState.CAMERA_INIT);

    // Create AR session via API
    const sessionData = await apiClient.createSession(posterId);

    // Store session ID
    app.session.sessionId = sessionData.sessionId;

    // State transition: CAMERA_INIT → DETECTING
    app.session.setState(SessionState.DETECTING);

    // Preload video during splash screen (parallel to improve perceived performance)
    console.log('🎬 Preloading video during splash screen...');

    // Create video element for AR video playback
    app.arVideoElement = document.createElement('video');
    app.arVideoElement.setAttribute('playsinline', '');
    app.arVideoElement.setAttribute('webkit-playsinline', '');
    app.arVideoElement.style.display = 'none'; // Hidden, used as texture
    document.body.appendChild(app.arVideoElement);

    // Initialize video controller
    app.videoController = new VideoController();
    // videoLoop opt-out: posterData.videoLoop === false のときのみループ無効。デフォルトはループ有効
    app.videoController.init(app.arVideoElement, { loop: posterData.videoLoop !== false });

    // Start video loading (will continue during UI interactions)
    // Support Cloudflare Stream HLS if configured
    const videoLoadPromise = app.videoController.load(
      posterData.videoId,
      posterData.videoUrl,
      (percent) => {
        // Update loading progress during splash screen
        console.log(`📹 Video preload progress: ${percent.toFixed(0)}%`);
      },
      null, // lowQualityUrl (not used for now)
      posterData.streamConfig // Cloudflare Stream config
    );

    // Load audio if needed (also in parallel)
    let audioLoadPromise = null;
    if (posterData.audioType === 'wav' && posterData.audioUrl) {
      app.audioController = new AudioController();
      app.audioController.init();
      audioLoadPromise = app.audioController.load(
        posterData.id + '-audio',
        posterData.audioUrl,
        'wav',
        (percent) => {
          console.log(`🔊 Audio preload progress: ${percent.toFixed(0)}%`);
        }
      );
    }

    // Hide splash screen immediately (multi-marker style)
    if (app.splashScreen) {
      console.log('🎬 Hiding splash screen immediately');
      app.splashScreen.hide(0); // No animation, match multi-marker
    }

    // Show audio consent dialog (while video loads in background)
    await showAudioConsentDialog();

    // Wait for video and audio to finish loading
    console.log('⏳ Waiting for media to finish loading...');
    app.videoLoadStartTime = performance.now(); // Store in app scope for callback access
    await videoLoadPromise;
    const videoLoadTime = performance.now() - app.videoLoadStartTime;
    console.log(
      '✅ Video preloaded:',
      app.arVideoElement.videoWidth,
      'x',
      app.arVideoElement.videoHeight
    );

    // Track video load completion
    const deliveryType = app.videoController.deliveryType || 'mp4';
    trackVideoLoad(posterData.videoId, videoLoadTime, deliveryType);

    if (audioLoadPromise) {
      await audioLoadPromise;
      console.log('✅ Audio preloaded (WAV)');
    }

    // Track AR session start
    trackARSessionStart(posterId, deliveryType);

    // Start poster detection and video playback
    await startPosterDetection(posterData);
  } catch (error) {
    console.error('Failed to handle direct poster access:', error);

    // Determine error type. isCameraDenied() が Issue #36 の MindAR undefined-reject を吸収する。
    let errorType = ErrorType.UNKNOWN_ERROR;
    if (isCameraDenied(error)) {
      errorType = ErrorType.CAMERA_ACCESS_DENIED;
    } else if (error instanceof ValidationError) {
      if (error.field === 'cameraPermission') {
        errorType = ErrorType.CAMERA_ACCESS_DENIED;
      }
    } else if (error.name === 'NotFoundError') {
      errorType = ErrorType.CAMERA_NOT_FOUND;
    } else if (error.name === 'NotReadableError') {
      errorType = ErrorType.CAMERA_IN_USE;
    } else if (error.status === 404) {
      errorType = ErrorType.POSTER_NOT_FOUND;
    } else if (error.name === 'TypeError' || error.status >= 500) {
      errorType = ErrorType.NETWORK_ERROR;
    }

    app.feedbackOverlay.hide();
    app.errorHandler.handleError(error, errorType, { posterId });

    trackError(errorType, error?.message ?? String(error));
  }
}

/**
 * Handle multi-marker poster using MultiMarkerARSession
 * @param {string} posterId - Poster ID
 * @param {Object} posterData - Poster configuration data
 */
async function handleMultiMarkerPoster(posterId, _posterData) {
  try {
    console.log('🎯 Initializing multi-marker AR session for:', posterId);

    // Issue #26: aura は単一ターゲット経路専用。誤設定で無音失敗しないよう明示
    if (_posterData?.effects?.aura?.enabled) {
      console.warn(
        '⚠️ [aura] effects.aura is not supported on multi-marker posters yet — ignored'
      );
    }

    // Hide splash screen immediately (multi-marker style)
    if (app.splashScreen) {
      app.splashScreen.hide(0); // No animation
    }
    app.feedbackOverlay.hide();

    // Note: Audio consent dialog is handled by MultiMarkerARSession.init()
    // Note: Loading overlay is shown by MultiMarkerARSession with "読み込み中..." message

    // Initialize MultiMarkerARSession
    app.multiMarkerSession = new MultiMarkerARSession({
      configPath: posterId, // Use posterId to load from posters.json
      containerElementId: 'mindar-container',
      onError: (error) => {
        console.error('❌ Multi-marker session error:', error);
        app.feedbackOverlay.showError(error.message || 'ARセッションでエラーが発生しました', [
          'ページをリロードして再試行してください',
        ]);
      },
      onMarkerChanged: (markerId) => {
        console.log('🎯 Active marker changed:', markerId);
        // Tracking is handled by MultiMarkerARSession internally
      },
    });

    // Initialize session
    console.log('⏳ Initializing multi-marker session...');
    await app.multiMarkerSession.init();
    console.log('✅ Multi-marker session initialized');

    // Start session
    console.log('▶️ Starting multi-marker session...');
    await app.multiMarkerSession.start();
    // 複数マーカー版は start() 内部でカメラを立ち上げるため、成功=許可済み
    trackCameraGrant(true);
    console.log('✅ Multi-marker session started');

    // Expose to global for debugging (DEV only)
    if (import.meta.env.DEV) {
      window.arSession = app.multiMarkerSession;
      console.log('🐛 Debug: arSession exposed to window.arSession');
    }

    // Session is now running
    console.log('✅ Multi-marker AR experience is live');
  } catch (error) {
    console.error('❌ Failed to initialize multi-marker poster:', error);

    // Safely extract error message
    const errorMessage = error?.message || error?.toString() || 'Unknown error';

    let errorType = ErrorType.UNKNOWN_ERROR;
    if (isCameraDenied(error)) {
      errorType = ErrorType.CAMERA_ACCESS_DENIED;
      trackCameraGrant(false);
    } else if (
      errorMessage.includes('camera') ||
      errorMessage.includes('getUserMedia') ||
      errorMessage.includes('Timeout')
    ) {
      errorType = ErrorType.CAMERA_ERROR;
      trackCameraGrant(false);
    } else if (errorMessage.includes('mind') || errorMessage.includes('Mind')) {
      errorType = ErrorType.TARGET_LOAD_ERROR;
    } else if (errorMessage.includes('video') || errorMessage.includes('Video')) {
      errorType = ErrorType.VIDEO_LOAD_ERROR;
    }

    app.feedbackOverlay.hide();
    app.errorHandler.handleError(new Error(errorMessage), errorType, {});
  }
}

/**
 * Start QR scanning flow (US1)
 */
async function startQRScanningFlow() {
  try {
    // Create video element for camera (hidden - used as texture source)
    if (!app.videoElement) {
      app.videoElement = document.createElement('video');
      app.videoElement.style.display = 'none';
      document.body.appendChild(app.videoElement);
    }

    // Initialize camera view
    app.cameraView = new CameraView();
    app.cameraView.init(app.videoElement);

    // Request camera permission
    app.feedbackOverlay.showLoading('カメラを起動中...', 20);
    const permissionGranted = await app.cameraView.requestPermission();
    trackCameraGrant(permissionGranted);

    if (!permissionGranted) {
      throw new ValidationError('Camera permission denied', 'cameraPermission');
    }

    // Start camera stream
    await app.cameraView.startStream();

    // Initialize QR scanner
    app.qrScanner = new QRScanner();
    await app.qrScanner.init(app.videoElement);

    // Start QR scanning
    app.feedbackOverlay.showLoading('QRコードをスキャン中...', 40);
    app.session.setState(SessionState.QR_SCANNING);

    app.qrScanner.start(
      // onQRDetected callback
      async (posterId) => {
        await handleQRDetected(posterId);
      },
      // onError callback
      (error) => {
        handleQRError(error);
      }
    );
  } catch (error) {
    console.error('Failed to start QR scanning:', error);

    // Determine error type
    let errorType = ErrorType.UNKNOWN_ERROR;
    if (isCameraDenied(error)) {
      errorType = ErrorType.CAMERA_ACCESS_DENIED;
    } else if (error instanceof ValidationError) {
      if (error.field === 'cameraPermission') {
        errorType = ErrorType.CAMERA_ACCESS_DENIED;
      }
    } else if (error.name === 'NotFoundError') {
      errorType = ErrorType.CAMERA_NOT_FOUND;
    } else if (error.name === 'NotReadableError') {
      errorType = ErrorType.CAMERA_IN_USE;
    }

    app.feedbackOverlay.hide();
    app.errorHandler.handleError(error, errorType, {});
  }
}

/**
 * Handle QR code detection (T031)
 */
async function handleQRDetected(posterId) {
  try {
    // Stop QR scanning
    if (app.qrScanner) {
      app.qrScanner.stop();
    }

    app.currentPosterId = posterId;

    // State transition: QR_SCANNING → CAMERA_INIT
    app.session.setState(SessionState.CAMERA_INIT);

    // Load poster data
    const posterData = await posterService.preloadPoster(posterId);

    // Create AR session via API
    const sessionData = await apiClient.createSession(posterId);

    // Store session ID
    app.session.sessionId = sessionData.sessionId;

    // State transition: CAMERA_INIT → DETECTING
    app.session.setState(SessionState.DETECTING);

    // Hide splash screen immediately (multi-marker style)
    if (app.splashScreen) {
      console.log('🎬 Hiding splash screen immediately');
      app.splashScreen.hide(0); // No animation, match multi-marker
    }

    // Show audio consent dialog after camera permission
    await showAudioConsentDialog();

    // Phase 4 (US2) - Start poster detection and video playback
    await startPosterDetection(posterData);
  } catch (error) {
    console.error('Failed to handle QR detection:', error);

    // Determine error type
    let errorType = ErrorType.UNKNOWN_ERROR;
    if (error?.status === 404) {
      errorType = ErrorType.POSTER_NOT_FOUND;
    } else if (error?.name === 'TypeError' || error?.status >= 500) {
      errorType = ErrorType.NETWORK_ERROR;
    }

    app.feedbackOverlay.hide();
    app.errorHandler.handleError(error, errorType, {
      posterId: app.currentPosterId,
    });
    // Note: Retry callback is handled by ErrorHandler's onRetry option if needed
  }
}

/**
 * Handle QR scanning error
 */
function handleQRError(error) {
  console.error('QR scanning error:', error);

  let errorType = ErrorType.UNKNOWN_ERROR;
  if (error instanceof ValidationError && error.field === 'qrCode') {
    errorType = ErrorType.QR_INVALID;
  }

  // Show error but keep scanning
  app.feedbackOverlay.showError(error.message, ['QRコードを再度スキャンしてください']);
}

/**
 * Show audio consent dialog and wait for user choice
 * @returns {Promise<void>}
 */
async function showAudioConsentDialog() {
  console.log('🔊 Showing audio consent dialog...');
  return new Promise((resolve) => {
    app.audioConsentDialog.show((isMuted) => {
      app.userAudioChoice = isMuted;
      console.log(`✅ User chose audio: ${isMuted ? 'muted' : 'unmuted'}`);
      resolve();
    });
  });
}

/**
 * Start poster detection and video playback (US2)
 */
async function startPosterDetection(posterData) {
  try {
    // Initialize poster detector with maxTargets option
    // Support multiple targets if posterData specifies, otherwise default to 1
    const maxTargets = posterData.targets?.length || posterData.maxTargets || 1;
    console.log(`📋 Initializing PosterDetector with ${maxTargets} target(s)`);
    app.posterDetector = new PosterDetector({ maxTargets });

    // Get target file path from poster data (uses targetUrl from posters.json)
    const targetPath = posterData.targetUrl;

    // Initialize MindAR detector
    await app.posterDetector.init(targetPath);

    // Get Three.js objects from MindAR
    const renderer = app.posterDetector.getRenderer();
    const scene = app.posterDetector.getScene();
    const camera = app.posterDetector.getCamera();
    const anchor = app.posterDetector.getAnchor();

    // Initialize AR renderer
    app.arRenderer = new ARRenderer();
    app.arRenderer.init(renderer, scene, camera);
    app.arRenderer.setupStencilBuffer();

    // Video and audio may already be loaded (from handleDirectPosterAccess preloading)
    // Only load if not already done (e.g., from QR scanning flow)
    if (!app.videoController || !app.arVideoElement) {
      console.log('📹 Video not preloaded, loading now...');

      // Create video element for AR video playback
      app.arVideoElement = document.createElement('video');
      app.arVideoElement.setAttribute('playsinline', '');
      app.arVideoElement.setAttribute('webkit-playsinline', '');
      app.arVideoElement.style.display = 'none'; // Hidden, used as texture
      document.body.appendChild(app.arVideoElement);

      // Initialize video controller
      app.videoController = new VideoController();
      app.videoController.init(app.arVideoElement, { loop: posterData.videoLoop !== false });

      // Load video directly from poster data (with Cloudflare Stream support)
      await app.videoController.load(
        posterData.videoId,
        posterData.videoUrl,
        null, // onProgress
        null, // lowQualityUrl
        posterData.streamConfig // Cloudflare Stream config
      );
      console.log(
        '✅ Video loaded:',
        app.arVideoElement.videoWidth,
        'x',
        app.arVideoElement.videoHeight
      );

      // Load audio based on poster configuration
      if (posterData.audioType === 'wav' && posterData.audioUrl) {
        // Use separate WAV audio file
        app.audioController = new AudioController();
        app.audioController.init();
        await app.audioController.load(posterData.id + '-audio', posterData.audioUrl, 'wav');
        console.log('✅ Audio loaded (WAV)');
      } else if (posterData.audioType === 'video') {
        // Use video embedded audio
        console.log('✅ Using video embedded audio');
      }
    } else {
      console.log('✅ Video already preloaded, skipping load');
    }

    // Initialize CTA overlay if present (structured cta or legacy overlayHtml)
    app.ctaOverlay = createCTAFromPosterData(posterData);
    if (app.ctaOverlay) {
      console.log('🔘 Initializing CTA overlay');
      const uiContainer = document.getElementById('ui-container');
      if (uiContainer) {
        app.ctaOverlay.mount(uiContainer);
      }
      // 動画完視聴 → CTA を固定表示にしてマーカーロスト無効化。
      // 従来はマーカーが画角外になると CTA も消え、動画を最後まで見たユーザーが
      // 直後にスマホを動かした瞬間クリック機会を失っていた(実測: 2026-07-16 15:32 セッション)。
      // arVideoElement 経由で ended を拾えばループ有無に関わらず全案件で効く。
      if (app.arVideoElement) {
        app.arVideoElement.addEventListener('ended', () => {
          app.ctaOverlay?.pin();
        });
      }
    }

    // Video planes will be created after MindAR starts and dimensions are extracted
    // (see code after app.posterDetector.start() below)

    // Note: Audio control is handled through user interaction, not automatic toggle
    // FeedbackOverlay doesn't provide audio toggle UI (simpler design)

    // Setup detection callbacks with pause/resume logic
    let isFirstDetection = true;
    let lostTimeout = null;
    let markerStartMs = null; // 現在のマーカー捕捉開始時刻。ロスト確定で null に戻す
    const LOST_DELAY_MS = 100; // Wait 100ms before pausing (reduced from 500ms for immediate stop)

    app.posterDetector.setCallbacks(
      // onDetected - called every frame when poster is tracked
      // targetIndex indicates which target (0-based) was detected
      async (matrix, targetIndex = 0) => {
        // Clear any pending pause timeout (poster found again)
        if (lostTimeout) {
          clearTimeout(lostTimeout);
          lostTimeout = null;
        }

        // 検出/再検出の各サイクル開始時刻。ロスト確定で null に戻り、次検出でまた入る
        if (markerStartMs === null) {
          markerStartMs = performance.now();
        }

        if (isFirstDetection) {
          console.log(
            `🎯 FIRST DETECTION - Target ${targetIndex} detected, starting video playback`
          );

          // Track marker detection time
          const detectionTime = performance.now() - app.videoLoadStartTime;
          trackMarkerDetection(posterData.id, detectionTime);

          // State transition: DETECTING → TRACKING
          app.session.setState(SessionState.TRACKING);

          // Note: FeedbackOverlay doesn't provide tracking indicator (simpler design)
          // Tracking state is maintained internally

          // Apply user's audio choice before starting playback
          if (app.userAudioChoice !== null) {
            if (app.audioController) {
              app.audioController.setMuted(app.userAudioChoice);
            }
            if (app.videoController) {
              app.videoController.setMuted(app.userAudioChoice);
            }
          }

          // Start video playback
          console.log('📹 Calling videoController.play()...');
          try {
            await app.videoController.play();
            console.log('✅ Video playback started successfully');

            // Issue #26: fade in aura when video actually starts playing
            if (app.aura) {
              app.aura.setActive(true);
            }
            // video_progress(0/25/50/75) と video_ended は VideoController が自動送信

            // Start audio playback if using separate audio
            if (app.audioController) {
              await app.audioController.play();
              console.log('✅ Audio playback started (WAV)');
            }

            // Note: Audio toggle button is not shown in single-marker mode
            // FeedbackOverlay provides simpler UI without audio controls
          } catch (error) {
            console.error('❌ Video playback failed:', error);
          }

          // Hide loading UI
          app.feedbackOverlay.hide();

          // Performance monitoring disabled (causes iOS jitter)

          isFirstDetection = false;
        } else if (!app.videoController.getIsPlaying()) {
          // Resume video if it was paused
          await app.videoController.play();

          // Resume audio if using separate audio
          if (app.audioController && !app.audioController.getIsPlaying()) {
            await app.audioController.play();
          }

          // Issue #26: fade in aura on resume too
          if (app.aura) {
            app.aura.setActive(true);
          }

          app.session.setState(SessionState.TRACKING);

          // Note: Tracking state update is handled internally
        }

        // Show CTA overlay if it exists
        if (app.ctaOverlay) {
          app.ctaOverlay.show();
        }

        // Video plane transform is handled by anchor.group automatically
        // No need to manually set videoPlane matrix

        // Note: Video texture update handled in renderer.render() every frame
        // iOS Safari requires every-frame needsUpdate for stable playback
      },
      // onLost - called when target is lost
      // targetIndex indicates which target (0-based) was lost
      (targetIndex = 0) => {
        console.log(`⚠️ Target ${targetIndex} lost`);

        // Note: Lost state is handled internally

        // Wait before pausing (tolerates brief tracking loss)
        lostTimeout = setTimeout(() => {
          // Pause video playback
          if (app.videoController) {
            app.videoController.pause();
          }

          // Pause audio playback
          if (app.audioController) {
            app.audioController.pause();
          }

          // Issue #26: fade out aura when playback pauses (aura stays through the 100ms grace)
          if (app.aura) {
            app.aura.setActive(false);
          }

          // Hide CTA overlay if it exists
          if (app.ctaOverlay) {
            app.ctaOverlay.hide();
          }

          // Update session state
          app.session.setState(SessionState.DETECTING);

          // Note: Searching state is handled internally

          // マーカー捕捉サイクルの終端。捕捉開始から今までの実時間を送る
          if (markerStartMs !== null) {
            trackMarkerEnd(posterData.id, performance.now() - markerStartMs);
            markerStartMs = null;
          }

          lostTimeout = null;
        }, LOST_DELAY_MS);
      }
    );

    // Start detection (MindAR handles its own render loop)

    // Note: FeedbackOverlay doesn't show tracking indicator during detection
    // Simpler UI design without persistent indicator

    // Start MindAR (this can take 10-30 seconds on mobile)
    console.log('🚀 Starting MindAR detector...');
    const startTime = performance.now();

    try {
      await app.posterDetector.start();
      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      console.log(`✅ MindAR detector started in ${elapsed}s`);
      // MindAR.start() 成功 = カメラ許可済み + ストリーム開始成功。
      // 単一マーカーフローで唯一 camera_grant が確定するタイミング(multi-marker/QRフローには別途送信済み)。
      trackCameraGrant(true);

      // Get auto-detected target dimensions from MindAR
      const targetDimensions = app.posterDetector.getTargetDimensions();
      if (!targetDimensions || targetDimensions.length === 0) {
        throw new Error('Failed to extract target dimensions from .mind file');
      }

      // Now create video planes using auto-detected dimensions
      console.log(`📐 Creating video planes using auto-detected dimensions`);
      const videoAspectRatio = app.arVideoElement.videoWidth / app.arVideoElement.videoHeight;

      // Support both array-based configuration (posterData.targets) and single target
      if (posterData.targets && Array.isArray(posterData.targets)) {
        // Array-based configuration: create video plane for each target
        console.log(`📐 Creating video planes for ${posterData.targets.length} targets`);

        for (let i = 0; i < posterData.targets.length; i++) {
          const targetConfig = posterData.targets[i];
          const targetAnchor = app.posterDetector.getAnchorByIndex(i);
          const targetDim = targetDimensions[i];

          if (!targetAnchor) {
            console.warn(`⚠️ No anchor found for target ${i}`);
            continue;
          }

          if (!targetDim) {
            console.warn(`⚠️ No dimension data found for target ${i}`);
            continue;
          }

          // Use auto-detected aspect ratio
          const targetAspectRatio = targetDim.aspectRatio;

          const targetWidth = 1.0;
          const targetHeight = targetWidth / targetAspectRatio;

          let videoPlaneWidth, videoPlaneHeight;

          // Get fit mode: "contain" (default) or "cover"
          const fitMode = targetConfig.fitMode || posterData.fitMode || 'contain';

          if (fitMode === 'cover') {
            // Cover logic: fill target, may crop video
            if (videoAspectRatio > targetAspectRatio) {
              // Video is wider than target - fit to height (crop sides)
              videoPlaneHeight = targetHeight;
              videoPlaneWidth = videoPlaneHeight * videoAspectRatio;
            } else {
              // Video is taller than target - fit to width (crop top/bottom)
              videoPlaneWidth = targetWidth;
              videoPlaneHeight = videoPlaneWidth / videoAspectRatio;
            }
          } else {
            // Contain logic: fit video inside target (default)
            if (videoAspectRatio > targetAspectRatio) {
              videoPlaneWidth = targetWidth;
              videoPlaneHeight = videoPlaneWidth / videoAspectRatio;
            } else {
              videoPlaneHeight = targetHeight;
              videoPlaneWidth = videoPlaneHeight * videoAspectRatio;
            }
          }

          console.log(
            `📐 Target ${i}: ${targetDim.width.toFixed(3)}x${targetDim.height.toFixed(
              3
            )} (aspect: ${targetAspectRatio.toFixed(3)}) - fitMode: ${fitMode}`
          );
          console.log(
            `📐 Video plane ${i}: ${videoPlaneWidth.toFixed(3)}x${videoPlaneHeight.toFixed(
              3
            )} units`
          );

          const videoPlane = app.arRenderer.createVideoPlane(
            app.arVideoElement,
            videoPlaneWidth,
            videoPlaneHeight
          );

          videoPlane.position.z = 0.01;
          targetAnchor.group.add(videoPlane);
          console.log(`✅ Video plane ${i} added to anchor ${i}`);

          // Issue #26: video aura (opt-in). First target only — screen layer is one per scene.
          if (i === 0) {
            attachVideoAuraIfEnabled(posterData, videoPlane, targetAnchor, scene);
          }
        }
      } else {
        // Single target configuration (backward compatible)
        const targetDim = targetDimensions[0];
        const targetAspectRatio = targetDim.aspectRatio;

        const targetWidth = 1.0;
        const targetHeight = targetWidth / targetAspectRatio;

        let videoPlaneWidth, videoPlaneHeight;

        // Get fit mode: "contain" (default) or "cover"
        const fitMode = posterData.fitMode || 'contain';

        if (fitMode === 'cover') {
          // Cover logic: fill target, may crop video
          if (videoAspectRatio > targetAspectRatio) {
            // Video is wider than target - fit to height (crop sides)
            videoPlaneHeight = targetHeight;
            videoPlaneWidth = videoPlaneHeight * videoAspectRatio;
          } else {
            // Video is taller than target - fit to width (crop top/bottom)
            videoPlaneWidth = targetWidth;
            videoPlaneHeight = videoPlaneWidth / videoAspectRatio;
          }
        } else {
          // Contain logic: fit video inside target (default)
          if (videoAspectRatio > targetAspectRatio) {
            videoPlaneWidth = targetWidth;
            videoPlaneHeight = videoPlaneWidth / videoAspectRatio;
          } else {
            videoPlaneHeight = targetHeight;
            videoPlaneWidth = videoPlaneHeight * videoAspectRatio;
          }
        }

        console.log(
          `📐 Target: ${targetDim.width.toFixed(3)}x${targetDim.height.toFixed(
            3
          )} (aspect: ${targetAspectRatio.toFixed(3)}) - fitMode: ${fitMode}`
        );
        console.log(
          `📐 Video: ${app.arVideoElement.videoWidth}x${app.arVideoElement.videoHeight} (aspect: ${videoAspectRatio.toFixed(
            3
          )})`
        );
        console.log(`📐 Target plane: ${targetWidth.toFixed(3)}x${targetHeight.toFixed(3)} units`);
        console.log(
          `📐 Video plane: ${videoPlaneWidth.toFixed(3)}x${videoPlaneHeight.toFixed(3)} units`
        );

        const videoPlane = app.arRenderer.createVideoPlane(
          app.arVideoElement,
          videoPlaneWidth,
          videoPlaneHeight
        );

        videoPlane.position.z = 0.01;
        anchor.group.add(videoPlane);
        console.log('✅ Video plane added to anchor group');

        // Issue #26: video aura (opt-in). Attach to first anchor + scene.
        attachVideoAuraIfEnabled(posterData, videoPlane, anchor, scene);
      }
    } catch (error) {
      console.error('❌ MindAR detector failed to start:', error);
      // カメラ拒否/未許可でここに落ちるケースを検知して camera_grant=false を送る。
      // Issue #36: MindAR は _startVideo の getUserMedia().catch で reject() を引数無しで
      // 呼ぶ経路がある (iOS Safari のサイト権限が「拒否」の時など)。そこから undefined が
      // 伝播してくるため、error===undefined もカメラ拒否確定として扱う。
      // MindAR は素の NotAllowedError を包む場合もあるためメッセージ含み判定でフォールバック。
      const errMsg = error?.message || '';
      if (
        isCameraDenied(error) ||
        errMsg.includes('Permission') ||
        errMsg.includes('permission') ||
        errMsg.includes('NotAllowed') ||
        errMsg.includes('getUserMedia') ||
        errMsg.includes('camera')
      ) {
        trackCameraGrant(false);
      }
      throw error;
    }

    // Splash screen already hidden before audio consent dialog
    // No need to hide again here

    // Setup MindAR render loop (REQUIRED for Three.js rendering)
    let lastFrameTime = performance.now();
    renderer.setAnimationLoop(() => {
      const now = performance.now();
      const dt = now - lastFrameTime;
      lastFrameTime = now;
      if (app.aura) {
        app.aura.update(dt);
      }
      renderer.render(scene, camera);
    });
    console.log('✅ MindAR render loop initialized');
  } catch (error) {
    console.error('Failed to start poster detection:', error);

    let errorType = ErrorType.DETECTION_FAILED;
    const errMsg = error?.message || '';
    if (isCameraDenied(error)) {
      errorType = ErrorType.CAMERA_ACCESS_DENIED;
    } else if (errMsg.includes('video')) {
      errorType = ErrorType.VIDEO_LOAD_ERROR;
    } else if (errMsg.includes('MindAR')) {
      errorType = ErrorType.DETECTION_FAILED;
    }

    app.feedbackOverlay.hide();
    app.errorHandler.handleError(error, errorType, {
      posterId: app.currentPosterId,
    });
    // Note: Retry callback is handled by ErrorHandler's onRetry option if needed
  }
}

/**
 * Cleanup function
 */
function cleanup() {
  if (app.qrScanner) {
    app.qrScanner.cleanup();
  }
  if (app.cameraView) {
    app.cameraView.cleanup();
  }
  if (app.posterDetector) {
    app.posterDetector.cleanup();
  }
  if (app.arRenderer) {
    app.arRenderer.cleanup();
  }
  if (app.videoController) {
    app.videoController.cleanup();
  }
  if (app.audioController) {
    app.audioController.cleanup();
  }
  if (app.feedbackOverlay) {
    app.feedbackOverlay.cleanup();
  }
  if (app.splashScreen) {
    app.splashScreen.cleanup();
  }
  if (app.audioConsentDialog) {
    app.audioConsentDialog.cleanup();
  }
  if (app.performanceMonitor) {
    app.performanceMonitor.stop();
  }
  if (app.aura) {
    app.aura.dispose();
    app.aura = null;
  }
}

/**
 * Issue #26 helper — attach video aura (ambilight glow).
 * デフォルトON（CEO決定 2026-07-15）。effects.aura.enabled:false の案件のみ
 * オプトアウトされ、その場合は 1 バイトも副作用を出さない。
 */
function attachVideoAuraIfEnabled(posterData, videoPlane, anchor, _scene) {
  const urlParams = new URLSearchParams(window.location.search);
  const auraConfig = resolveAuraConfig(posterData, urlParams);
  if (!auraConfig) return;

  const videoTexture = app.arRenderer?.videoTexture;
  if (!videoTexture) {
    console.warn('⚠️ [aura] videoTexture not available, skipping aura setup');
    return;
  }

  // 再入対策: startPosterDetection がリトライ経路で再実行されても leak しないよう既存を捨てる
  if (app.aura) {
    app.aura.dispose();
    app.aura = null;
  }

  const videoSize = {
    width: videoPlane.geometry.parameters.width,
    height: videoPlane.geometry.parameters.height,
  };
  const aura = createVideoAura(videoTexture, auraConfig, videoSize);
  anchor.group.add(aura.sourceMesh);

  // 動画プレーンをオーラより後に描いて鮮明さを守る。オプトイン外なら1バイトも触らない。
  videoPlane.material.transparent = true;
  videoPlane.material.needsUpdate = true;
  videoPlane.renderOrder = 10;

  app.aura = aura;
  console.log('✨ [aura] enabled', auraConfig);
}

/**
 * Top-level error boundary
 */
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error);
  if (app.errorHandler) {
    app.errorHandler.handleError(event.error, ErrorType.UNKNOWN_ERROR, {});
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  if (app.errorHandler) {
    app.errorHandler.handleError(
      event.reason || new Error('Unhandled promise rejection'),
      ErrorType.UNKNOWN_ERROR,
      {}
    );
  }
});

// Page Visibility API - pause videos when browser goes to background
// This prevents unwanted video playback (and CloudFront bandwidth usage)
// when users switch tabs, minimize browser, or lock their phone screen
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    console.log('📱 Page hidden - pausing all videos to save bandwidth');
    // Multi-marker session
    if (app.multiMarkerSession?.videoController) {
      app.multiMarkerSession.videoController.pauseAll();
    }
    // Single-marker session
    if (app.videoController) {
      app.videoController.pause();
    }
    // Audio controller
    if (app.audioController) {
      app.audioController.pause();
    }
    // Issue #26: keep aura in sync with video state so return-from-background
    // doesn't briefly show "aura full while video paused"
    if (app.aura) {
      app.aura.setActive(false);
    }
  }
  // Note: We intentionally don't auto-resume on visibility change
  // User needs to re-point camera at marker to resume playback
  // This is the expected UX for AR experiences
});

// Cleanup on page unload
window.addEventListener('beforeunload', cleanup);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initARExperience);
} else {
  initARExperience();
}

export { cleanup, initARExperience };
